import { removeTokensMessage, type StateError, TEMPLATE_TOKENS, UNAVAILABLE_VARIABLE_MESSAGE } from './validateState'

export interface IssueSummary {
  title: string
  status: string
}

export interface IssueLine {
  text: string
  numbers: number[]
}

const plural = (count: number, word: string): string => `${count} ${word}${count === 1 ? '' : 's'}`

const unique = <T>(values: T[]): T[] => Array.from(new Set(values))

const slotIdsOf = (errors: StateError[]): string[] => unique(errors.flatMap((error) => (error.slotId ? [error.slotId] : [])))

export const issueNumbers = (errors: StateError[]): Map<string, number> => new Map(slotIdsOf(errors).map((slotId, index) => [slotId, index + 1]))

const groupKey = (error: StateError): string => (error.kind === 'variable' ? 'variable' : (error.rule ?? error.message))

const lineText = (key: string, group: StateError[]): string => {
  const oneField = slotIdsOf(group).length <= 1
  switch (key) {
    case 'variable':
      return unique(group.flatMap((error) => (error.variable ? [error.variable] : []))).length > 1
        ? "Remove the highlighted variables. They can't be used in this email."
        : UNAVAILABLE_VARIABLE_MESSAGE
    case 'empty':
      return oneField ? 'Fill in this field.' : 'Fill in these fields.'
    case 'tooLong':
      return oneField ? 'Shorten this field.' : 'Shorten these fields.'
    case 'templateLogic': {
      const typed = group.flatMap((error) => error.tokens ?? [])
      return removeTokensMessage(TEMPLATE_TOKENS.filter((token) => typed.includes(token)))
    }
    default:
      return group[0].message
  }
}

export const issueLines = (errors: StateError[]): IssueLine[] => {
  const numbers = issueNumbers(errors)
  const groups = errors.reduce<Map<string, StateError[]>>(
    (byKey, error) => byKey.set(groupKey(error), [...(byKey.get(groupKey(error)) ?? []), error]),
    new Map(),
  )
  return Array.from(groups.entries()).map(([key, group]) => ({
    text: lineText(key, group),
    numbers: slotIdsOf(group).flatMap((slotId) => numbers.get(slotId) ?? []),
  }))
}

export const issueSummary = (errors: StateError[]): IssueSummary | null => {
  if (!errors.length) {
    return null
  }
  const fields = issueNumbers(errors).size
  return fields
    ? { title: `Fix ${plural(fields, 'field')} to save`, status: `${plural(fields, 'field')} to fix` }
    : { title: 'Fix this email to save', status: 'Fix this email to save' }
}
